import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  Users,
  BookOpen,
  FileQuestion,
  Award,
  Settings,
  Building2,
  CreditCard,
  BarChart3,
  Mail,
  Palette,
  GraduationCap,
  LogOut,
  FileText,
  Menu as MenuIcon,
  UserPlus,
  Home,
  Image,
  FileCheck,
  DollarSign,
  Shield,
  Tag,
  Briefcase,
  Database,
  Trash2 as Trash2Icon,
  Newspaper,
} from 'lucide-react';

const Sidebar = ({ role, onNavigate }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user, api } = useAuth();
  const [pendingApprovals, setPendingApprovals] = useState(0);
  useEffect(() => {
    if (role !== 'super_admin') return;
    api.get('/course-review/queue').then((r) => setPendingApprovals(r.data?.total || 0)).catch(() => {});
  }, [api, role, location.pathname]);

  const getMenuItems = () => {
    switch (role) {
      case 'super_admin':
        return [
          { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
          { icon: Home, label: 'Edit Homepage', path: '/admin/homepage' },
          { icon: Users, label: 'Users', path: '/admin/users' },
          { icon: Building2, label: 'Organisations', path: '/admin/organisations' },
          { icon: BookOpen, label: 'Courses', path: '/admin/courses' },
          { icon: FileCheck, label: 'Approvals', path: '/admin/approvals', badge: pendingApprovals },
          { icon: Tag, label: 'Categories', path: '/admin/categories' },
          { icon: Newspaper, label: 'Knowledge Hub', path: '/admin/knowledge-hub' },
          { icon: FileQuestion, label: 'Question Bank', path: '/admin/questions' },
          { icon: FileText, label: 'Pages', path: '/admin/pages' },
          { icon: Image, label: 'Media Library', path: '/admin/media' },
          { icon: Award, label: 'Certificates', path: '/admin/certificates' },
          { icon: FileCheck, label: 'Cert Templates', path: '/admin/certificates/template' },
          { icon: CreditCard, label: 'Subscription Tiers', path: '/admin/subscription-tiers' },
          { icon: BarChart3, label: 'Reports', path: '/admin/reports' },
          { icon: Shield, label: 'Compliance', path: '/admin/compliance' },
          { icon: Database, label: 'Data Management', path: '/admin/data' },
          { icon: Settings, label: 'Settings', path: '/admin/settings' },
        ];
      case 'content_manager':
        return [
          { icon: LayoutDashboard, label: 'Dashboard', path: '/cms' },
          { icon: BookOpen, label: 'Courses', path: '/cms/courses' },
          { icon: Tag, label: 'Categories', path: '/admin/categories' },
          { icon: Newspaper, label: 'Knowledge Hub', path: '/admin/knowledge-hub' },
          { icon: FileText, label: 'Pages', path: '/admin/pages' },
          { icon: Image, label: 'Media Library', path: '/admin/media' },
          { icon: FileQuestion, label: 'Question Bank', path: '/cms/questions' },
          { icon: Award, label: 'Certificate Templates', path: '/cms/certificates' },
          { icon: Trash2Icon, label: 'Trash & Versions', path: '/admin/data' },
        ];
      case 'org_admin':
        return [
          { icon: LayoutDashboard, label: 'Dashboard', path: '/organisation' },
          { icon: Users, label: 'Team Members', path: '/organisation/team' },
          { icon: BookOpen, label: 'Courses', path: '/organisation/courses' },
          { icon: BarChart3, label: 'Reports', path: '/organisation/reporting' },
          { icon: CreditCard, label: 'Subscription', path: '/organisation/subscription' },
          { icon: Settings, label: 'Settings', path: '/organisation/settings' },
        ];
      default:
        return [
          { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
          { icon: BookOpen, label: 'My Courses', path: '/my-courses' },
          { icon: GraduationCap, label: 'Browse Courses', path: '/courses' },
          { icon: Award, label: 'Certificates', path: '/certificates' },
          ...(user?.is_resident ? [{ icon: Briefcase, label: 'My Passport', path: '/passport' }] : []),
          { icon: Settings, label: 'Settings', path: '/profile' },
        ];
    }
  };

  const menuItems = getMenuItems();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <aside className="w-64 min-h-screen bg-[#2a3e5d] text-white flex flex-col" data-testid="sidebar">
      {/* Logo */}
      <div className="p-6 border-b border-white/10">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-9 h-9 bg-[#2a3e5d] ring-2 ring-white/20 rounded-lg flex items-center justify-center">
            <GraduationCap className="w-5 h-5 text-white" />
          </div>
          <div>
            <span className="font-semibold text-base block">Common Impact</span>
            <span className="text-xs text-[#02d1d4] capitalize">{role?.replace('_', ' ')}</span>
          </div>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1">
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path || 
                          (item.path !== '/admin' && item.path !== '/cms' && item.path !== '/organisation' && item.path !== '/dashboard' && location.pathname.startsWith(item.path));
          const Icon = item.icon;
          
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => onNavigate && onNavigate()}
              className={cn(
                'flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors',
                isActive
                  ? 'bg-[#20acf6]/20 text-[#20acf6]'
                  : 'text-white/60 hover:bg-white/5 hover:text-white'
              )}
              data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <Icon className="w-4.5 h-4.5" />
              {item.label}
              {item.badge > 0 && (
                <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-[#F4A261] text-[#1e293b] text-[11px] font-bold flex items-center justify-center" data-testid="sidebar-approvals-count">{item.badge}</span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="p-4 border-t border-white/10">
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-white/50 hover:bg-white/5 hover:text-white transition-colors w-full"
          data-testid="sidebar-logout"
        >
          <LogOut className="w-4.5 h-4.5" />
          Logout
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
